<template>
  <div class="text-body-editor">
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
      <div class="menubar">
        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold"
        >
          <!-- <i class="fa fa-bold"></i> -->
          <font-awesome-icon :icon="['fas', 'bold']" />
          <!-- <icon name="bold" /> -->
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic"
        >
          <!-- <icon name="italic" /> -->
          <font-awesome-icon :icon="['fas', 'italic']" />
          <!-- <i class="fa fa-italic"></i> -->
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() }"
          @click="commands.underline"
        >
          <!-- <icon name="underline" /> -->
          <font-awesome-icon :icon="['fas', 'underline']" />
          <!-- <i class="fa fa-underline"></i> -->
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.paragraph() }"
          @click="commands.paragraph"
        >
          <!-- <icon name="paragraph" /> -->
          <font-awesome-icon :icon="['fas', 'paragraph']" />
          <!-- <i class="fa fa-paragraph"></i> -->
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })"
        >H1</button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })"
        >H2</button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click="commands.heading({ level: 3 })"
        >H3</button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 4 }) }"
          @click="commands.heading({ level: 4 })"
        >H4</button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 5 }) }"
          @click="commands.heading({ level: 5 })"
        >H5</button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 6 }) }"
          @click="commands.heading({ level: 6 })"
        >H6</button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click="commands.bullet_list"
        >
          <!-- <icon name="ul" /> -->
          <font-awesome-icon :icon="['fas', 'list-ul']" />
          <!-- <i class="fa fa-list-ul"></i> -->
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click="commands.ordered_list"
        >
          <!-- <icon name="ol" /> -->
          <font-awesome-icon :icon="['fas', 'list-ol']" />
          <!-- <i class="fa fa-list-ol"></i> -->
        </button>

        <button class="menubar__button" @click="commands.horizontal_rule">
          <!-- <icon name="hr" /> -->
          -
        </button>

        <button class="menubar__button" @click="commands.undo">
          <!-- <icon name="undo" /> -->
          <font-awesome-icon :icon="['fas', 'undo']" />
          <!-- <i class="fa fa-undo" aria-hidden="true"></i> -->
        </button>

        <button class="menubar__button" @click="commands.redo">
          <font-awesome-icon :icon="['fas', 'redo']" />
          <!-- <i class="fa fa-repeat" aria-hidden="true"></i> -->
          <!-- <icon name="redo" /> -->
        </button>
      </div>
    </editor-menu-bar>

    <editor-content :editor="this.editor" />
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  HardBreak,
  Heading,
  OrderedList,
  BulletList,
  ListItem,
  Bold,
  Italic,
  Link,
  Underline,
  HorizontalRule,
  Strike,
  History,
} from "tiptap-extensions";

import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faBold,
  faItalic,
  faStrikethrough,
  faUnderline,
  faParagraph,
  faListOl,
  faListUl,
  faUndo,
  faRedo,
} from "@fortawesome/free-solid-svg-icons";

library.add(
  faBold,
  faItalic,
  faStrikethrough,
  faUnderline,
  faParagraph,
  faListOl,
  faListUl,
  faUndo,
  faRedo
);

export default {
  name: "TextBodyEditor",
  props: {
    content: String,
  },
  components: { Editor, EditorContent, EditorMenuBar },
  data() {
    return {
      editor: null,
    };
  },
  mounted() {
    this.editor = new Editor({
      content: this.content,
      onUpdate: ({ getHTML }) => {
        this.$emit("textchange", getHTML());
      },
      extensions: [
        new HardBreak(),
        new Heading({ levels: [1, 2, 3, 4, 5, 6] }),
        new OrderedList(),
        new BulletList(),
        new ListItem(),
        new Bold(),
        new Italic(),
        new Link(),
        new Underline(),
        new HorizontalRule(),
        new History(),
      ],
    });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss" scoped>
.editor__content li > p {
  display: inline;
}

.menubar {
  text-align: center;
  border-radius: 7px;
  padding: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.menubar__button {
  padding: 2px;
  width: 32px;
  height: 32px;
  border: none;
  border-top: 1px solid black;
  border-bottom: 1px solid black;
  border-right: 1px solid gray;

  &.is-active {
    background-color: lightgray;
  }

  &:hover {
    background-color: lightgray;
    cursor: pointer;
  }

  &:first-of-type {
    border-radius: 7px 0px 0px 7px;
    border-left: 1px solid black;
  }

  &:last-of-type {
    border-radius: 0px 7px 7px 0px;
    border-right: 1px solid black;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/TextBodyEditor.vue